<script>
import { mapGetters } from 'vuex';
import {
  getLastMessage,
  getLastMessageIncludingPrivate,
} from 'dashboard/helper/conversationHelper';
import { isMissedCall } from 'dashboard/helper/phoneHelper';
import Thumbnail from '../Thumbnail.vue';
import MessagePreview from './MessagePreview.vue';
import router from '../../../routes';
import {
  frontendURL,
  conversationUrl,
  conversationListQuery,
} from '../../../helper/URLHelper';
import InboxName from '../InboxName.vue';
import ConversationID from '../ConversationID.vue';
import inboxMixin from 'shared/mixins/inboxMixin';
import ConversationContextMenu from './contextMenu/Index.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import CardLabels from './conversationCardComponents/CardLabels.vue';
import PriorityMark from './PriorityMark.vue';
import SlaCountdownBadge from './components/SlaCountdownBadge.vue';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import wootConstants from 'dashboard/constants/globals';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

export default {
  components: {
    CardLabels,
    InboxName,
    ConversationID,
    Thumbnail,
    ConversationContextMenu,
    TimeAgo,
    MessagePreview,
    PriorityMark,
    SlaCountdownBadge,
    ContextMenu,
  },
  mixins: [inboxMixin],
  props: {
    activeLabel: {
      type: String,
      default: '',
    },
    chat: {
      type: Object,
      default: () => {},
    },
    hideInboxName: {
      type: Boolean,
      default: false,
    },
    hideThumbnail: {
      type: Boolean,
      default: false,
    },
    teamId: {
      type: [String, Number],
      default: 0,
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
    showAssignee: {
      type: Boolean,
      default: false,
    },
    conversationType: {
      type: String,
      default: '',
    },
    selected: {
      type: Boolean,
      default: false,
    },
    enableContextMenu: {
      type: Boolean,
      default: false,
    },
    // The context menu offers only opening and copying the conversation link
    contextMenuLinksOnly: {
      type: Boolean,
      default: false,
    },
    isOnExpandedLayout: {
      type: Boolean,
      default: false,
    },
    // Contact history: open conversations get a bold blue inbox and id.
    highlightOpen: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'contextMenuToggle',
    'assignAgent',
    'assignLabel',
    'assignTeam',
    'markAsUnread',
    'markAsRead',
    'assignPriority',
    'updateConversationStatus',
  ],
  data() {
    return {
      hovered: false,
      showContextMenu: false,
      contextMenu: {
        x: null,
        y: null,
      },
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      inboxesList: 'inboxes/getInboxes',
      activeInbox: 'getSelectedInbox',
      accountId: 'getCurrentAccountId',
      currentAccount: 'accounts/getCurrentAccount',
      uiSettings: 'getUISettings',
    }),
    readingUsers() {
      return this.$store.getters['conversationReadingStatus/getUserList'](
        this.chat.id
      );
    },
    visibleReadingUsers() {
      return this.readingUsers.slice(0, 3);
    },
    hiddenReadingUsersCount() {
      return Math.max(0, this.readingUsers.length - 3);
    },
    readingUsersTooltip() {
      return this.readingUsers.map(u => u.name).join(', ');
    },
    chatMetadata() {
      return this.chat.meta || {};
    },

    assignee() {
      return this.chatMetadata.assignee || {};
    },

    currentContact() {
      return this.$store.getters['contacts/getContact'](
        this.chatMetadata.sender.id
      );
    },

    isActiveChat() {
      return this.currentChat.id === this.chat.id;
    },

    unreadCount() {
      return this.chat.unread_count;
    },

    hasUnread() {
      return this.unreadCount > 0;
    },

    openHighlightClass() {
      const isOpen = this.chat.status === wootConstants.STATUS_TYPE.OPEN;
      return this.highlightOpen && isOpen ? '!text-n-blue-text font-bold' : '';
    },

    isInboxNameVisible() {
      // Always show for Channel::Phone regardless of active inbox
      if (this.inbox?.channel_type === 'Channel::Phone') {
        return true;
      }
      return !this.activeInbox;
    },

    lastMessageInChat() {
      return getLastMessage(this.chat);
    },

    // Same condition that paints the missed call icon in InboxName, so the
    // preview text is highlighted exactly when that icon shows up.
    isMissedCallConversation() {
      return (
        this.inbox?.channel_type === 'Channel::Phone' && isMissedCall(this.chat)
      );
    },

    messagePreviewTextClass() {
      if (this.isMissedCallConversation) return 'font-bold text-n-ruby-11';
      return this.hasUnread ? 'font-medium text-n-slate-12' : 'text-n-slate-11';
    },

    // The call state says more than the last message ("Inbound call from … to
    // …"), so a missed call replaces the preview content altogether.
    messagePreviewOverride() {
      if (!this.isMissedCallConversation) return '';
      return this.$t('CONVERSATION.MESSAGE_EVENT.VOICE.COPILOT.CALL_MISSED');
    },

    emailPreviewWithNote() {
      return this.uiSettings.email_preview_format === 'subject_with_note';
    },

    lastNoteInChat() {
      if (!this.emailPreviewWithNote) {
        return null;
      }
      if (!this.isAnEmailChannel) {
        return null;
      }
      const lastMessage = getLastMessageIncludingPrivate(this.chat);
      if (!lastMessage?.private) {
        return null;
      }
      // when the subject line has no subject to fall back to, it renders the
      // note itself — avoid showing the same note twice
      if (
        lastMessage.id === this.lastMessageInChat?.id &&
        !this.chat.mail_subject
      ) {
        return null;
      }
      return lastMessage;
    },

    inbox() {
      const { inbox_id: inboxId } = this.chat;
      const stateInbox = this.$store.getters['inboxes/getInbox'](inboxId);
      return stateInbox;
    },
    showInboxName() {
      return (
        !this.hideInboxName &&
        this.isInboxNameVisible &&
        (this.inboxesList.length > 1 ||
          this.inbox?.channel_type === 'Channel::Phone')
      );
    },
    inboxNameDisplayMode() {
      if (
        this.activeInbox === this.inbox?.id &&
        this.inbox?.channel_type === 'Channel::Phone'
      ) {
        return 'icon';
      }
      return 'full';
    },
    inboxName() {
      const stateInbox = this.inbox;
      return stateInbox.name || '';
    },
    conversationSlaPolicies() {
      return this.chat?.sla_policy || [];
    },
    hasSlaPolicies() {
      return this.conversationSlaPolicies.length > 0;
    },
    // The conversation within the current list (inbox, label, team, folder and filters)
    conversationPath() {
      return frontendURL(
        conversationUrl({
          accountId: this.accountId,
          activeInbox: this.activeInbox,
          id: this.chat.id,
          label: this.activeLabel,
          teamId: this.teamId,
          foldersId: this.foldersId,
          conversationType: this.conversationType,
        }),
        conversationListQuery(this.$route?.query)
      );
    },
  },
  methods: {
    onCardClick(e) {
      const path = this.conversationPath;

      if (e.metaKey || e.ctrlKey) {
        this.openInNewTab();
        return;
      }
      if (this.isActiveChat) {
        return;
      }

      router.push(path);
    },
    onThumbnailHover() {
      this.hovered = !this.hideThumbnail;
    },
    onThumbnailLeave() {
      this.hovered = false;
    },
    onSelectConversation(checked) {
      const action = checked ? 'selectConversation' : 'deSelectConversation';
      this.$emit(action, this.chat.id, this.inbox.id);
    },
    openContextMenu(e) {
      if (!this.enableContextMenu) return;
      e.preventDefault();
      this.$emit('contextMenuToggle', true);
      this.contextMenu.x = e.pageX || e.clientX;
      this.contextMenu.y = e.pageY || e.clientY;
      this.showContextMenu = true;
    },
    closeContextMenu() {
      this.$emit('contextMenuToggle', false);
      this.showContextMenu = false;
      this.contextMenu.x = null;
      this.contextMenu.y = null;
    },
    onUpdateConversation(status, snoozedUntil) {
      this.closeContextMenu();
      this.$emit(
        'updateConversationStatus',
        this.chat.id,
        status,
        snoozedUntil
      );
    },
    async onAssignAgent(agent) {
      this.$emit('assignAgent', agent, [this.chat.id]);
      this.closeContextMenu();
    },
    async onAssignLabel(label) {
      this.$emit('assignLabel', [label.title], [this.chat.id]);
      this.closeContextMenu();
    },
    async onAssignTeam(team) {
      this.$emit('assignTeam', team, this.chat.id);
      this.closeContextMenu();
    },
    async markAsUnread() {
      this.$emit('markAsUnread', this.chat.id);
      this.closeContextMenu();
    },
    async markAsRead() {
      this.$emit('markAsRead', this.chat.id);
      this.closeContextMenu();
    },
    async assignPriority(priority) {
      this.$emit('assignPriority', priority, this.chat.id);
      this.closeContextMenu();
    },
    openInNewTab() {
      window.open(
        window.chatwootConfig.hostURL + this.conversationPath,
        '_blank',
        'noopener noreferrer nofollow'
      );
    },
    onMenuOpenInNewTab() {
      this.closeContextMenu();
      this.openInNewTab();
    },
    // A shared link opens the conversation on its own, not within this agent's list
    async copyLink() {
      const path = frontendURL(
        conversationUrl({ accountId: this.accountId, id: this.chat.id })
      );
      this.closeContextMenu();
      await copyTextToClipboard(window.chatwootConfig.hostURL + path);
      useAlert(this.$t('CONVERSATION.CARD_CONTEXT_MENU.COPY_LINK_SUCCESS'));
    },
  },
};
</script>

<template>
  <div
    class="relative flex items-start flex-grow-0 flex-shrink-0 w-auto max-w-full px-4 py-0 border-t-0 border-b-0 border-l-2 border-r-0 border-transparent border-solid cursor-pointer conversation hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3 group"
    :class="{
      'active animate-card-select bg-n-alpha-1 dark:bg-n-alpha-3 border-n-weak':
        isActiveChat,
      'unread-chat': hasUnread,
      'has-inbox-name': showInboxName,
      'conversation-selected': selected,
    }"
    @click="onCardClick"
    @contextmenu="openContextMenu($event)"
  >
    <div
      class="relative"
      @mouseenter="onThumbnailHover"
      @mouseleave="onThumbnailLeave"
    >
      <label
        v-if="hovered || selected"
        class="checkbox-wrapper absolute inset-0 z-20 backdrop-blur-[2px]"
        @click.stop
      >
        <input
          :value="selected"
          :checked="selected"
          class="checkbox"
          type="checkbox"
          @change="onSelectConversation($event.target.checked)"
        />
      </label>
      <Thumbnail
        v-if="!hideThumbnail"
        :src="currentContact.thumbnail"
        :badge="inboxBadge"
        :username="currentContact.name"
        :status="currentContact.availability_status"
        size="40px"
      />
    </div>
    <div
      class="px-0 py-3 border-b group-hover:border-transparent flex-1 border-n-slate-3 w-[calc(100%-40px)]"
    >
      <div class="flex justify-between conversation-card--meta">
        <div
          class="flex min-w-0 max-w-[calc(100%-6.875rem)] py-0.5 px-0 leading-3 whitespace-nowrap bg-none text-slate-600 dark:text-slate-500 text-xs my-0"
        >
          <InboxName
            v-if="showInboxName"
            :inbox="inbox"
            :conversation="chat"
            :display-mode="inboxNameDisplayMode"
            :class="openHighlightClass"
          />
          <ConversationID
            :chat="chat"
            class="flex-shrink-0"
            :class="openHighlightClass"
          />
        </div>
        <div class="flex gap-2 ml-2 rtl:mr-2 rtl:ml-0">
          <span
            v-if="showAssignee && assignee.name"
            class="text-n-slate-11 text-xs font-medium leading-3 py-0.5 px-0 inline-flex text-ellipsis overflow-hidden whitespace-nowrap max-w-64"
          >
            <fluent-icon icon="person" size="12" class="text-n-slate-11" />
            {{ assignee.name }}
          </span>
          <SlaCountdownBadge
            v-if="hasSlaPolicies"
            :sla-policies="conversationSlaPolicies"
            :priority="chat.priority"
            :status="chat.status"
            class="relative -top-0.5"
          />
          <PriorityMark :priority="chat.priority" />
        </div>
      </div>
      <h4
        class="conversation--user text-sm my-0 mx-2 pt-0.5 text-ellipsis overflow-hidden whitespace-nowrap w-[calc(100%-110px)] text-n-slate-12"
        :class="hasUnread ? 'font-semibold' : 'font-medium'"
      >
        {{ currentContact.name }}
      </h4>
      <MessagePreview
        v-if="lastMessageInChat"
        :conversation="chat"
        :message="lastMessageInChat"
        :show-message-type="!lastNoteInChat"
        :content-override="messagePreviewOverride"
        class="conversation--message my-0 mx-2 leading-6 h-6 text-sm"
        :class="[
          messagePreviewTextClass,
          isOnExpandedLayout
            ? 'max-w-[96%] w-[80%]'
            : 'max-w-[96%] w-[16.875rem]',
        ]"
      />
      <p
        v-else
        class="conversation--message text-n-slate-11 text-sm my-0 mx-2 leading-6 h-6 max-w-[96%] w-[16.875rem] overflow-hidden text-ellipsis whitespace-nowrap"
        :class="hasUnread ? 'font-medium text-n-slate-12' : 'text-n-slate-11'"
      >
        <fluent-icon
          size="16"
          class="-mt-0.5 align-middle inline-block text-n-slate-10"
          icon="info"
        />
        <span>
          {{ $t(`CHAT_LIST.NO_MESSAGES`) }}
        </span>
      </p>
      <MessagePreview
        v-if="lastNoteInChat"
        :message="lastNoteInChat"
        class="conversation--message my-0 mx-2 leading-6 h-6 text-sm text-n-slate-11"
        :class="
          isOnExpandedLayout
            ? 'max-w-[96%] w-[80%]'
            : 'max-w-[96%] w-[16.875rem]'
        "
      />
      <div
        class="absolute flex flex-col conversation--meta ltr:right-4 rtl:left-4 top-4"
      >
        <span class="ml-auto font-normal leading-4 text-xxs">
          <TimeAgo
            :timestamp-display-mode="
              currentAccount.conversation_timestamp_display_mode
            "
            :last-activity-timestamp="chat.timestamp"
            :created-at-timestamp="chat.created_at"
            :last-customer-message-timestamp="chat.last_customer_message_at"
          />
        </span>
        <span
          class="unread shadow-lg rounded-full hidden text-xxs font-semibold h-4 leading-4 ml-auto mt-1 min-w-[1rem] px-1 py-0 text-center text-white bg-n-teal-9"
        >
          {{ unreadCount > 9 ? '9+' : unreadCount }}
        </span>
      </div>
      <CardLabels :conversation-labels="chat.labels" class="mt-0.5 mx-2 mb-0" />
    </div>
    <div
      v-if="readingUsers.length"
      class="absolute group/readers flex items-center ltr:right-4 rtl:left-4 bottom-[15px]"
    >
      <Thumbnail
        v-for="reader in visibleReadingUsers"
        :key="reader.id"
        :src="reader.thumbnail"
        :username="reader.name"
        size="16px"
        class="-ml-1 first:ml-0 ring-1 ring-white dark:ring-slate-800 rounded-full"
      />
      <span
        v-if="hiddenReadingUsersCount > 0"
        class="-ml-1 flex items-center justify-center w-4 h-4 rounded-full bg-n-slate-3 dark:bg-slate-600 text-n-slate-11 text-xxs font-semibold ring-1 ring-white dark:ring-slate-800"
      >
        +{{ hiddenReadingUsersCount }}
      </span>
      <div
        class="absolute bottom-5 right-0 z-10 hidden group-hover/readers:block bg-slate-900 dark:bg-slate-700 text-white text-xxs rounded px-2 py-1 whitespace-nowrap"
      >
        {{ readingUsersTooltip }}
      </div>
    </div>
    <ContextMenu
      v-if="showContextMenu"
      :x="contextMenu.x"
      :y="contextMenu.y"
      @close="closeContextMenu"
    >
      <ConversationContextMenu
        :status="chat.status"
        :inbox-id="inbox.id"
        :priority="chat.priority"
        :chat-id="chat.id"
        :has-unread-messages="hasUnread"
        :links-only="contextMenuLinksOnly"
        @update-conversation="onUpdateConversation"
        @assign-agent="onAssignAgent"
        @assign-label="onAssignLabel"
        @assign-team="onAssignTeam"
        @mark-as-unread="markAsUnread"
        @mark-as-read="markAsRead"
        @assign-priority="assignPriority"
        @open-in-new-tab="onMenuOpenInNewTab"
        @copy-link="copyLink"
      />
    </ContextMenu>
  </div>
</template>

<style lang="scss" scoped>
.conversation {
  &.unread-chat {
    .unread {
      @apply block;
    }
  }

  &.compact {
    @apply pl-0;

    .conversation-card--meta {
      @apply ltr:pr-4 rtl:pl-4;
    }

    .conversation--details {
      @apply rounded-sm ml-0 pl-5 pr-2;
    }
  }

  &::v-deep .user-thumbnail-box {
    @apply mt-4;
  }

  &.conversation-selected {
    @apply bg-n-slate-2 dark:bg-n-slate-3;
  }

  &.has-inbox-name {
    &::v-deep .user-thumbnail-box {
      @apply mt-8;
    }

    .checkbox-wrapper {
      @apply mt-8;
    }
  }

  .conversation--meta {
    @apply mt-4;
  }

  .checkbox-wrapper {
    @apply flex items-center justify-center rounded-full cursor-pointer mt-4;

    input[type='checkbox'] {
      @apply m-0 cursor-pointer;
    }
  }
}
</style>
